import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider, FlowingWaves, MandalaOrnament } from '@/components/feature/DrawingLines';

// ── Data ──────────────────────────────────────────────────────────────────────

const marketingSupports = [
  {
    icon: 'ri-instagram-line',
    title: 'SNS 통합 마케팅',
    desc: '본사 공식 인스타그램·유튜브 채널을 통한 가맹점 포트폴리오 노출. 월 평균 팔로워 도달 50만 명 이상.',
    tag: 'SNS',
  },
  {
    icon: 'ri-search-line',
    title: '검색 광고 공동 집행',
    desc: '네이버·구글 키워드 광고를 본사가 직접 집행. 지역 타겟팅으로 가맹점 주변 고객 유입 극대화.',
    tag: 'Search AD',
  },
  {
    icon: 'ri-camera-line',
    title: '전문 포트폴리오 촬영',
    desc: '오픈 초기 3개월, 본사 전속 포토그래퍼가 작품 촬영 지원. 고퀄리티 콘텐츠로 SNS 경쟁력 확보.',
    tag: 'Photo',
  },
  {
    icon: 'ri-map-pin-line',
    title: '지역 오프라인 마케팅',
    desc: '오픈 이벤트 기획·현수막·전단 디자인 제공. 지역 인플루언서 협업 연결 서비스.',
    tag: 'Local',
  },
  {
    icon: 'ri-star-line',
    title: '리뷰 관리 시스템',
    desc: '네이버 플레이스·카카오맵 리뷰 관리 가이드 제공. 부정 리뷰 대응 매뉴얼 및 본사 지원.',
    tag: 'Review',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: '월별 마케팅 리포트',
    desc: '광고 성과·유입 채널·예약 전환율 등 데이터 기반 월간 리포트 제공. 전략 개선 컨설팅 포함.',
    tag: 'Report',
  },
];

const logisticsSupports = [
  {
    icon: 'ri-truck-line',
    title: '정기 소모품 정기 배송',
    desc: '멸균 니들, 잉크, 위생 소모품 등 필수 소모품을 월 1회 정기 배송. 재고 부족 걱정 없이 운영 가능.',
    tag: 'Delivery',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: '공동 구매 할인',
    desc: '전국 가맹점 물량을 통합 구매하여 시중가 대비 30~40% 할인된 가격으로 프리미엄 소모품 공급.',
    tag: 'Discount',
  },
  {
    icon: 'ri-shield-check-line',
    title: '본사 인증 소모품만 공급',
    desc: '피부과 검증 잉크, 의료용 멸균 니들 등 본사가 직접 품질 검증한 소모품만 공급. 품질 보증.',
    tag: 'Quality',
  },
  {
    icon: 'ri-store-2-line',
    title: '장비 유지보수 지원',
    desc: '타투 머신, 멸균기 등 주요 장비 정기 점검 서비스. 고장 시 대여 장비 긴급 지원.',
    tag: 'Maintenance',
  },
  {
    icon: 'ri-archive-line',
    title: '재고 관리 시스템',
    desc: '전용 앱을 통한 소모품 재고 실시간 관리. 자동 발주 알림으로 재고 부족 사전 방지.',
    tag: 'Inventory',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '24시간 긴급 지원',
    desc: '소모품 긴급 부족 시 당일 특급 배송 서비스. 전담 물류 담당자 직통 연결.',
    tag: 'Emergency',
  },
];

const tabs = [
  { id: 'marketing', label: '마케팅 지원', path: '/franchise/support/marketing' },
  { id: 'logistics', label: '물류 및 소모품', path: '/franchise/support/logistics' },
];

const stats = [
  { value: '월 500만 원', label: '마케팅 지원 효과', sub: '본사 공동 집행 기준' },
  { value: '30~40%', label: '소모품 할인율', sub: '시중가 대비' },
  { value: '24시간', label: '긴급 지원', sub: '365일 운영' },
  { value: '100%', label: '인증 소모품', sub: '피부과 검증 완료' },
];

// ── Hero ──────────────────────────────────────────────────────────────────────

function SupportHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <section className="relative pt-40 pb-24 bg-[#1A1A1A] overflow-hidden">
      <div className="absolute inset-0 opacity-50">
        <img
          src="https://public.readdy.ai/ai/img_res/311d63efdc41ad937b30d47eca245da1.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-transparent to-[#1A1A1A]/60" />
      <FlowingWaves color="rgba(201,169,110,0.1)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.2)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-16 text-center">
        <div
          className="flex items-center justify-center gap-2 mb-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}
        >
          <Link to="/franchise" className="text-[10px] tracking-[0.3em] uppercase text-white/30 hover:text-white/60 transition-colors cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>Franchise</Link>
          <span className="text-white/20 text-xs">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]" style={{ fontFamily: "'Montserrat', sans-serif" }}>본사 지원 시스템</span>
        </div>

        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out 0.15s' }}>
          <h1 className="text-4xl md:text-6xl text-white mb-6 leading-tight" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.1em' }}>
            본사 지원 시스템
          </h1>
          <p className="text-sm md:text-base text-white/50 leading-relaxed max-w-lg mx-auto" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.8', wordBreak: 'keep-all' }}>
            선율은 가맹점주가 시술에만 집중할 수 있도록<br />
            마케팅부터 물류까지 모든 것을 지원합니다.
          </p>
        </div>

        <div className="mx-auto mt-12 h-px bg-white/15 transition-all duration-1000 ease-out" style={{ width: visible ? '80px' : '0px', transitionDelay: '500ms' }} />

        <div
          className="mt-12 grid grid-cols-2 md:flex md:flex-row items-center justify-center gap-8 md:gap-20"
          style={{ opacity: visible ? 1 : 0, transition: 'all 1s ease-out 0.6s' }}
        >
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-xl md:text-3xl text-white mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }}>{s.value}</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/30" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.label}</p>
              <p className="text-[9px] text-white/20 mt-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tab Section ───────────────────────────────────────────────────────────────

function SupportContent() {
  const location = useLocation();
  const { ref, visible } = useScrollAnimation(0.05);

  const activeTab = location.pathname.includes('logistics') ? 'logistics' : 'marketing';
  const items = activeTab === 'marketing' ? marketingSupports : logisticsSupports;

  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-left" color="rgba(201,169,110,0.08)" size={150} />
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.08)" size={140} />

      <div className="max-w-[1440px] mx-auto px-5 md:px-16">
        {/* Tab switcher */}
        <div
          className="flex justify-center mb-14"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease-out' }}
        >
          <div className="inline-flex border border-[#E5E5E5] p-1 rounded-full">
            {tabs.map((tab) => (
              <Link
                key={tab.id}
                to={tab.path}
                className={`px-6 py-2.5 text-[11px] tracking-[0.18em] uppercase font-medium rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#1A1A1A] text-white'
                    : 'text-[#888] hover:text-[#222222]'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Section header */}
        <div
          className="text-center mb-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out 0.1s' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>
            {activeTab === 'marketing' ? 'Marketing Support' : 'Logistics & Supply'}
          </p>
          <h2 className="text-2xl md:text-4xl text-[#222222]" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>
            {activeTab === 'marketing' ? '마케팅 지원 시스템' : '물류 및 소모품 지원'}
          </h2>
          <p className="text-sm text-[#888] max-w-md mx-auto mt-4 leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75', wordBreak: 'keep-all' }}>
            {activeTab === 'marketing'
              ? '본사가 직접 마케팅을 집행합니다. 가맹점주는 시술에만 집중하세요.'
              : '검증된 소모품을 합리적인 가격에. 재고 걱정 없이 운영하세요.'}
          </p>
        </div>

        <div className="mb-12">
          <GoldLineDivider delay={0.2} />
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <div
              key={item.title}
              className="group border border-[#EBEBEB] p-7 md:p-8 hover:border-[#222222] transition-all duration-300 cursor-default relative overflow-hidden"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s',
                transitionDelay: `${i * 80 + 200}ms`,
              }}
            >
              <div className="absolute inset-0 bg-[#1A1A1A] transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-5">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#EBEBEB] group-hover:border-white/20 group-hover:bg-white/10 transition-all duration-300">
                    <i className={`${item.icon} text-lg text-[#AAAAAA] group-hover:text-[#C9A96E] transition-colors duration-300`} />
                  </div>
                  <span className="text-[9px] px-2.5 py-1 bg-[#F5F5F5] group-hover:bg-white/10 text-[#888] group-hover:text-white/50 rounded-full whitespace-nowrap transition-all duration-300" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {item.tag}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-[#222222] group-hover:text-white mb-3 transition-colors duration-300" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>
                  {item.title}
                </h3>
                <p className="text-xs text-[#888] group-hover:text-white/60 leading-relaxed transition-colors duration-300" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75', wordBreak: 'keep-all' }}>
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Overview Section ──────────────────────────────────────────────────────────

function OverviewSection() {
  const { ref, visible } = useScrollAnimation(0.1);

  return (
    <section className="py-20 md:py-28 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.1)" size={150} />
      <div className="max-w-[1440px] mx-auto px-5 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 1s ease-out' }}>
            <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Why 선율 Support</p>
            <h2 className="text-2xl md:text-4xl text-[#222222] mb-6 leading-tight" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>
              혼자 하지 않아도 됩니다.<br />
              <span className="text-[#AAAAAA]">선율이 함께합니다.</span>
            </h2>
            <p className="text-sm text-[#888] leading-relaxed mb-8" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75', wordBreak: 'keep-all' }}>
              창업 후 가장 어려운 것은 마케팅과 운영입니다. 선율은 가맹점주가 오직 예술에만 집중할 수 있도록, 본사가 마케팅·물류·교육·컨설팅을 전담합니다.
            </p>
            <div className="flex flex-col gap-4">
              {[
                { icon: 'ri-check-line', text: '오픈 초기 3개월 집중 마케팅 지원' },
                { icon: 'ri-check-line', text: '월 로열티 50만 원에 모든 지원 포함' },
                { icon: 'ri-check-line', text: '전담 컨설턴트 1:1 배정' },
                { icon: 'ri-check-line', text: '24시간 긴급 지원 시스템' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#C9A96E] text-sm`} />
                  </div>
                  <span className="text-sm text-[#555]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", wordBreak: 'keep-all' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(40px)', transition: 'all 1s ease-out 0.2s' }}>
            <img
              src="https://readdy.ai/api/search-image?query=professional%20business%20team%20support%20meeting%20modern%20bright%20office%20interior%20Seoul%20Korea%20premium%20corporate%20workspace%20clean%20minimal%20design%20warm%20lighting%20editorial%20photography&width=700&height=560&seq=support_overview1&orientation=landscape"
              alt="선율 본사 지원팀"
              className="w-full h-[360px] md:h-[420px] object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────

function SupportCta() {
  const { ref, visible } = useScrollAnimation(0.2);
  return (
    <section className="py-24 bg-[#1A1A1A] overflow-hidden relative" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={3} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.18)" size={150} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />
      <div
        className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-16 text-center"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}
      >
        <div className="flex justify-center mb-8" style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease 0.3s' }}>
          <MandalaOrnament size={60} color="rgba(201,169,110,0.3)" delay={0.3} />
        </div>
        <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Start Together</p>
        <h2 className="text-2xl md:text-4xl text-white mb-6 leading-tight" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>
          지원 시스템을 직접<br />
          <span className="text-white/40">경험해보세요.</span>
        </h2>
        <p className="text-sm text-white/40 leading-relaxed max-w-md mx-auto mb-10" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75', wordBreak: 'keep-all' }}>
          가맹 문의 시 본사 지원 시스템 전체를 상세히 안내해드립니다.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/community/inquiry"
            className="w-full sm:w-auto inline-flex items-center justify-center px-10 py-4 bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            가맹 문의하기
          </Link>
          <Link
            to="/franchise/guide"
            className="w-full sm:w-auto inline-flex items-center justify-center px-10 py-4 border border-white/20 text-white/60 text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            창업 안내 보기
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FranchiseSupportPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <SupportHero />
        <SupportContent />
        <OverviewSection />
        <SupportCta />
      </main>
      <Footer />
    </div>
  );
}
